import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import NavBar from "../Componentes/NavBar";
import GenericBody from "../Componentes/GenericBody";
import Overlay from "../Componentes/Overlay";
import SideMenu from "../Componentes/SideMenu";
import SideMenuBtn from "../Componentes/SideMenuBtn";
import SortableTable from "../Componentes/Utils/SortableTable";

import ModalNovoCentroCusto from "../Modulos/FolgaCampoV2/ModalNovoCentroCusto";
import ModalAcoesObra from "../Modulos/FolgaCampoV2/ModalAcoesObra";
import ModalCadastroDistancias from "../Modulos/FolgaCampoV2/ModalCadastroDistancias";
import ModalCadastroFaixas from "../Modulos/FolgaCampoV2/ModalCadastroFaixas";

import { useModal } from "../contexts/ModalContext";
import { useToast } from "../contexts/ToastContext";
import useApi from "../hooks/useApi";

const PageStretch = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
    }
`;

const ActionBtn = styled.button`
    border: 1px solid #d1d5db;
    background: #f8fafc;
    color: #1f2937;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: 0.2s;

    &:hover {
        background: #e2e8f0;
        border-color: #94a3b8;
    }
`;

const FolgaCampoV2 = () => {

    const api = useApi();
    const { openModal, closeModal, isModalOpen } = useModal();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    /** Centro de custo (obra) selecionado para abrir os modais. */
    const [selected, setSelected] = useState(null);

    const obrasJaCadastradas = useMemo(() => items.map((i) => i.obra), [items]);

    const fetchCentros = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/Painel/API/FolgasCampo/CentroCustoFolga/List/index.php");
            if (data?.success && Array.isArray(data.items)) {
                setItems(data.items);
            } else {
                setItems([]);
                if (data?.message) notifyError(data.message);
            }
        } catch (e) {
            console.error(e);
            notifyError(e?.response?.data?.message || "Não foi possível carregar os centros de custo.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCentros();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openSideBar = () => openModal("folgaV2Sidebar");

    const openCreate = () => {
        closeModal();
        openModal("folgaV2Create");
    };

    const openAcoes = (row) => {
        setSelected(row);
        openModal("folgaV2Acoes");
    };

    const openDistancias = () => {
        closeModal();
        openModal("folgaV2Distancias");
    };

    const openFaixas = () => {
        closeModal();
        openModal("folgaV2Faixas");
    };

    const columns = useMemo(
        () => [
            {
                id: "obra",
                header: "CENTRO DE CUSTO (OBRA)",
                accessor: "obra",
                sortable: true,
            },
            {
                id: "dias_prazo",
                header: "DIAS PRAZO",
                accessor: "dias_prazo",
                sortable: true,
                width: "120px",
                align: "center",
            },
            {
                id: "acoes",
                header: "AÇÕES",
                accessor: () => null,
                sortable: false,
                width: "140px",
                align: "center",
                render: (_v, row) => (
                    <ActionBtn type="button" onClick={() => openAcoes(row)}>
                        GERENCIAR
                    </ActionBtn>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <>
            <NavBar title="GER. FOLGAS (v2)" setSideBar={openSideBar} modulo="rh" />
            <GenericBody>
                <PageStretch>
                    <TableFlexFill>
                        <SortableTable
                            columns={columns}
                            data={items}
                            isLoading={loading}
                            loadingText="Carregando…"
                            emptyText="Nenhum centro de custo cadastrado em centro_custo_folga."
                            initialSort={{ key: "obra", direction: "asc" }}
                            getRowId={(row) => row.id}
                        />
                    </TableFlexFill>
                </PageStretch>
            </GenericBody>

            {isModalOpen("folgaV2Sidebar") && (
                <Overlay state changeContent={closeModal}>
                    <SideMenu>
                        <SideMenuBtn button="NOVO CENTRO DE CUSTO" clickFunc={openCreate} />
                    </SideMenu>
                </Overlay>
            )}

            {isModalOpen("folgaV2Create") && (
                <ModalNovoCentroCusto
                    onSaved={fetchCentros}
                    obrasJaCadastradas={obrasJaCadastradas}
                />
            )}

            {isModalOpen("folgaV2Acoes") && (
                <ModalAcoesObra
                    obra={selected}
                    onCadastrarDistancias={openDistancias}
                    onCadastrarValores={openFaixas}
                />
            )}

            {isModalOpen("folgaV2Distancias") && (
                <ModalCadastroDistancias obra={selected} />
            )}

            {isModalOpen("folgaV2Faixas") && (
                <ModalCadastroFaixas obra={selected} />
            )}
        </>
    );
};

export default FolgaCampoV2;
